import { useLanguage } from "@/context/LanguageContext";
import { BRAND } from "@/data/translations";
import { CheckCircle2 } from "lucide-react";

const BioPortrait = () => (
    <div className="relative">
        <div className="absolute -inset-2 border border-fw/40 rounded-sm pointer-events-none" />
        <div className="relative aspect-[3/4] md:aspect-[4/5] overflow-hidden bg-black">
            <picture>
                <source srcSet={BRAND.lucaPhotoWebp} type="image/webp" />
                <img
                    src={BRAND.lucaPhoto}
                    alt="Luca La Capria — Personal Trainer"
                    data-testid="luca-photo"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: "50% 8%" }}
                    loading="lazy"
                    decoding="async"
                    width="600"
                    height="800"
                />
            </picture>
        </div>
        <div className="mt-5 flex items-center justify-between gap-3">
            <div className="flex flex-col">
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-fw">
                    Personal Trainer · NonSoloFitness
                </span>
                <p className="font-display text-base md:text-lg font-semibold mt-1 text-white">
                    Luca La Capria
                </p>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/45">
                Genova · Italia
            </span>
        </div>
    </div>
);

const BioCredentials = ({ credentials }) => (
    <div
        data-testid="bio-credentials"
        className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10"
    >
        {credentials.map((c) => (
            <div key={c.label} className="bg-black px-4 py-5 flex flex-col justify-between min-h-[110px]">
                <span className="font-heavy text-3xl md:text-4xl text-fw uppercase tabular-nums leading-none whitespace-nowrap">
                    {c.value}
                </span>
                <span className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-white/55 leading-snug">
                    {c.label}
                </span>
            </div>
        ))}
    </div>
);

const BioCertifications = ({ certifications }) => (
    <div className="mt-6 pt-5 border-t border-white/10">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-fw block mb-4">
            Certificazioni · Formazione
        </span>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2.5" data-testid="bio-certifications">
            {certifications.map((cert) => (
                <li key={cert} className="flex items-start gap-2.5 text-[13px] text-white/75 leading-snug">
                    <CheckCircle2 className="w-3.5 h-3.5 text-fw mt-0.5 shrink-0" strokeWidth={2} />
                    <span>{cert}</span>
                </li>
            ))}
        </ul>
    </div>
);

export const Bio = () => {
    const { t } = useLanguage();

    return (
        <section
            id="bio"
            data-testid="bio-section"
            className="relative bg-black text-white py-16 md:py-20 lg:py-24 overflow-hidden"
        >
            <div className="absolute -top-20 right-0 w-[420px] h-[420px] rounded-full bg-fw/10 blur-[140px] pointer-events-none" />
            <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />

            <div className="relative z-10 mx-auto max-w-[1440px] px-5 md:px-10 lg:px-16">
                <div className="grid grid-cols-12 gap-6 lg:gap-12 items-start">
                    <div className="col-span-12 lg:col-span-5 lg:sticky lg:top-32 self-start">
                        <BioPortrait />
                        <BioCertifications certifications={t.bio.certifications} />
                    </div>

                    <div className="col-span-12 lg:col-span-7 lg:col-start-6 lg:pl-4">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="w-10 h-px bg-fw" />
                            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-fw">
                                {t.bio.overline}
                            </span>
                        </div>

                        <h2
                            data-testid="bio-headline"
                            className="font-heavy text-5xl md:text-6xl lg:text-7xl leading-[0.95] uppercase mb-4"
                        >
                            <span className="block">Luca</span>
                            <span className="block">La Capria</span>
                        </h2>

                        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-fw mb-6">
                            {t.bio.role}
                        </p>

                        <div className="space-y-4 max-w-2xl">
                            {t.bio.paragraphs.map((para, i) => (
                                <p
                                    key={para}
                                    data-testid={`bio-paragraph-${i}`}
                                    className="text-base md:text-lg text-white/75 leading-relaxed"
                                >
                                    {para}
                                </p>
                            ))}
                        </div>

                        <BioCredentials credentials={t.bio.credentials} />
                    </div>
                </div>
            </div>
        </section>
    );
};
